import React, { useEffect, useRef } from "react";

export default function WebCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setTimeout(() => {
      const gl = canvas.getContext('webgl2');
      gl.viewport(0, 0, canvas.width, canvas.height);
      window.currentGL = gl;

      const vertexShader = `#version 300 es
        uniform mat4 uMF, uMI;
        in vec3 aPos, aNor;
        in vec2 aUV;
        out vec3 vPos, vNor;
        out vec2 vUV;
        void main() {
          vec4 pos = uMF * vec4(aPos, 1.);
          vec4 nor = vec4(aNor, 0.) * uMI;
          gl_Position = pos * vec4(1.,1.,-.1,1.);
          vPos = pos.xyz;
          vNor = nor.xyz;
          vUV = aUV;
        }
      `;

      const fragmentShader = `#version 300 es
        precision highp float;
        in vec3 vPos, vNor;
        in vec2 vUV;
        out vec4 fragColor;
        uniform vec3 uColor;
        uniform sampler2D uSampler;

        void main() {
          vec3 nor = normalize(vNor);
          float c = 0.1 + max(0., dot(vec3(0.5), nor)) + max(0., dot(vec3(-0.5), nor));
          vec4 T = texture(uSampler, vUV);
          fragColor = vec4(sqrt(c) * uColor * T.rgb, 1.);
        }
      `;

      // Compile shaders
      const compile = (type, src) => {
        const shader = gl.createShader(type);
        gl.shaderSource(shader, src);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
          console.error('Shader error:', gl.getShaderInfoLog(shader));
        }
        return shader;
      };

      const program = gl.createProgram();
      gl.attachShader(program, compile(gl.VERTEX_SHADER, vertexShader));
      gl.attachShader(program, compile(gl.FRAGMENT_SHADER, fragmentShader));
      gl.linkProgram(program);
      gl.useProgram(program);

      // Create sphere mesh
      const createMesh = (nu, nv) => {
        let mesh = [];
        for (let j = nv; j > 0; j--) {
          for (let i = 0; i <= nu; i++) {
            const u1 = i / nu, v1 = j / nv;
            const u2 = i / nu, v2 = (j - 1) / nv;
            
            for (const [u, v] of [[u1, v1], [u2, v2]]) {
              const theta = Math.PI * 2 * u;
              const phi = Math.PI * (v - 0.5);
              const x = Math.cos(phi) * Math.cos(theta);
              const y = Math.cos(phi) * Math.sin(theta);
              const z = Math.sin(phi);
              mesh.push(x, y, z, x, y, z, u, v);
            }
          }
          const u1 = 1, v1 = (j - 1) / nv;
          const theta1 = Math.PI * 2 * u1;
          const phi1 = Math.PI * (v1 - 0.5);
          const x1 = Math.cos(phi1) * Math.cos(theta1);
          const y1 = Math.cos(phi1) * Math.sin(theta1);
          const z1 = Math.sin(phi1);
          mesh.push(x1, y1, z1, x1, y1, z1, u1, v1);
          
          const u0 = 0, v0 = v1;
          const theta0 = 0;
          const phi0 = Math.PI * (v0 - 0.5);
          const x0 = Math.cos(phi0) * Math.cos(theta0);
          const y0 = Math.cos(phi0) * Math.sin(theta0);
          const z0 = Math.sin(phi0);
          mesh.push(x0, y0, z0, x0, y0, z0, u0, v0);
        }
        return new Float32Array(mesh);
      };

      const sphereData = createMesh(40, 20);
      const buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, sphereData, gl.STATIC_DRAW);

      // Setup vertex attributes
      const vertexSize = 8;
      const setupAttrib = (name, size, offset) => {
        const attr = gl.getAttribLocation(program, name);
        gl.enableVertexAttribArray(attr);
        gl.vertexAttribPointer(attr, size, gl.FLOAT, false, vertexSize * 4, offset * 4);
      };
      setupAttrib('aPos', 3, 0);
      setupAttrib('aNor', 3, 3);
      setupAttrib('aUV', 2, 6);

      // Load texture
      const texture = gl.createTexture();
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
        console.log('Texture loaded');
      };
      img.src = '/assets/texture0.jpg';

      gl.enable(gl.DEPTH_TEST);
      gl.depthFunc(gl.LEQUAL);

      // MATRIX HELPER FUNCTIONS
      const identity = () => [1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1];
      const mxm = (a, b) => {
        let m = [];
        for (let c = 0; c < 16; c += 4)
          for (let r = 0; r < 4; r++)
            m.push(a[r]*b[c] + a[r+4]*b[c+1] + a[r+8]*b[c+2] + a[r+12]*b[c+3]);
        return m;
      };
      const turnX = t => [1,0,0,0, 0,Math.cos(t),Math.sin(t),0, 0,-Math.sin(t),Math.cos(t),0, 0,0,0,1];
      const turnY = t => [Math.cos(t),0,-Math.sin(t),0, 0,1,0,0, Math.sin(t),0,Math.cos(t),0, 0,0,0,1];
      const scale = (x,y,z) => [x,0,0,0, 0,y??x,0,0, 0,0,z??x,0, 0,0,0,1];
      const perspective = (x,y,z) => [1,0,0,x, 0,1,0,y??x, 0,0,1,z??x, 0,0,0,1];
      const inverse = src => {
        let dst = [], det = 0, cofactor = (c, r) => {
          let s = (i, j) => src[c+i & 3 | (r+j & 3) << 2];
          return (c+r & 1 ? -1 : 1) * ( (s(1,1)*(s(2,2)*s(3,3)-s(3,2)*s(2,3)))
                                      - (s(2,1)*(s(1,2)*s(3,3)-s(3,2)*s(1,3)))
                                      + (s(3,1)*(s(1,2)*s(2,3)-s(2,2)*s(1,3))) );
        }
        for (let n = 0; n < 16; n++) dst.push(cofactor(n >> 2, n & 3));
        for (let n = 0; n < 4; n++) det += src[n] * dst[n << 2];
        for (let n = 0; n < 16; n++) dst[n] /= det;
        return dst;
      };

      // ANIMATION
      const render = () => {
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        
        const time = Date.now() / 1000;
        const m = mxm(perspective(0, 0, -0.5), mxm(turnY(time / 2), mxm(turnX(Math.PI / 2), scale(0.5))));
        
        gl.uniformMatrix4fv(gl.getUniformLocation(program, 'uMF'), false, m);
        gl.uniformMatrix4fv(gl.getUniformLocation(program, 'uMI'), false, inverse(m));
        gl.uniform3fv(gl.getUniformLocation(program, 'uColor'), [1, 1, 1]);
        
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.uniform1i(gl.getUniformLocation(program, 'uSampler'), 0);
        
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, sphereData.length / vertexSize);
        requestAnimationFrame(render);
      };
      render();
    }, 100);

    return () => {};
  }, []);

  return (
    <canvas 
      ref={canvasRef} 
      width={600} 
      height={600}
      style={{ display: 'block', background: 'black' }} 
    />
  );
}